import type {
  Agendamento,
  Cliente,
  EventoHistorico,
  GuiaVersao,
  HorarioDisponivel,
  Instalacao,
  Novidade,
  StatusContrato,
  TagVersao,
  VersaoDisponivel,
  VersoesInstalacao,
} from '@/data/types';

/**
 * Normalizadores das respostas da API do PAN.
 *
 * As formas irregulares tratadas aqui não são invenção: são exatamente as que o
 * app do monorepo já precisava absorver (ver `src/api/*.ts` do pan-app), e por
 * isso continuam valendo. A diferença é que aqui elas ficam isoladas numa única
 * camada, testável e sem acoplamento com a renderização.
 *
 *  - /instalacoes        pode vir como `[ ... ]` ou como `[[ ... ]]` (aninhado);
 *                        o `id` lógico pode chegar em `tenant` em vez de `id`.
 *  - /agendamentos       pode vir como array OU como mapa { instalacao: [...] }.
 *  - /historico-…        array, { result }, { items }, { historico } ou mapa.
 *  - /agendamentos/versoes  `{ versoes: [{ value, placeholder }] }`; lista vazia
 *                        significa "sem atualizações disponíveis", não erro.
 *  - /agendamentos/disponibilidade  `{ data, horarios: [{horario,disponivel,total}] }`.
 */

type Raw = Record<string, unknown>;

const str = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : value == null ? fallback : String(value));
const nullableStr = (value: unknown): string | null => (value == null || value === '' ? null : String(value));
const num = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/** Desembrulha `[[...]]`, `{result}`, `{items}`, `{<chave>: [...]}` num array plano. */
export function toRows(payload: unknown, chaveDeAgrupamento?: string): Raw[] {
  if (Array.isArray(payload)) {
    if (payload.length > 0 && Array.isArray(payload[0])) return (payload[0] as Raw[]) ?? [];
    return payload as Raw[];
  }
  if (payload && typeof payload === 'object') {
    const record = payload as Raw;
    for (const chave of ['result', 'items', 'historico', 'agendamentos', 'novidades']) {
      if (Array.isArray(record[chave])) return record[chave] as Raw[];
    }
    // Mapa { "<instalacao>": [ ...itens ] } — achata preservando a chave.
    return Object.entries(record).flatMap(([chaveGrupo, itens]) => {
      if (!Array.isArray(itens)) return [];
      return (itens as Raw[]).map((item) =>
        chaveDeAgrupamento && item[chaveDeAgrupamento] == null ? { ...item, [chaveDeAgrupamento]: chaveGrupo } : item,
      );
    });
  }
  return [];
}

function toCliente(raw: unknown): Cliente | null {
  if (!raw || typeof raw !== 'object') return null;
  const cliente = raw as Raw;
  const contratoRaw = cliente.contrato as Raw | undefined;
  return {
    id: nullableStr(cliente.id),
    nome: nullableStr(cliente.nome),
    contrato: contratoRaw
      ? {
          id: nullableStr(contratoRaw.id),
          status: nullableStr(contratoRaw.status) as StatusContrato | null,
          dataContratacao: nullableStr(contratoRaw.dataContratacao),
          modulos: (Array.isArray(contratoRaw.modulos) ? (contratoRaw.modulos as Raw[]) : []).map((modulo, index) => ({
            id: str(modulo.id, `modulo-${index}`),
            nome: str(modulo.nome),
            inicio: nullableStr(modulo.inicio),
            saude: nullableStr(modulo.saude),
          })),
        }
      : null,
  };
}

export function toInstalacao(raw: Raw): Instalacao {
  return {
    // O backend às vezes identifica a instalação pelo `tenant`.
    id: str(raw.tenant ?? raw.id),
    codigo: nullableStr(raw.codigo),
    nome: str(raw.nome),
    clienteId: str(raw.clienteId),
    cliente: toCliente(raw.cliente),
    tipo: (nullableStr(raw.tipo) as Instalacao['tipo']) ?? null,
    regiao: nullableStr(raw.regiao),
    versaoAtual: str(raw.versaoAtual),
    versaoEstavelRecomendada: str(raw.versaoEstavelRecomendada),
    ultimaAtualizacao: nullableStr(raw.ultimaAtualizacao),
    saude: (nullableStr(raw.saude) as Instalacao['saude']) ?? null,
    papel: (nullableStr(raw.papel) as Instalacao['papel']) ?? null,
    administrador: raw.administrador === true,
    isControladora: raw.isControladora === true,
    controladoraId: nullableStr(raw.controladoraId),
    cloudCode: nullableStr(raw.cloud_code ?? raw.cloudCode),
    atualizar: raw.atualizar === true,
    ana: raw.ana === true,
    interno: raw.interno === true,
    escopo: {
      total: nullableStr(raw.total_escopo),
      texto: nullableStr(raw.texto_escopo),
      saude: nullableStr(raw.saude_escopo),
      saudeDescricao: nullableStr(raw.saude_descricao),
      modo: nullableStr(raw.modo),
    },
  };
}

export function toAgendamento(raw: Raw): Agendamento {
  return {
    id: str(raw.id),
    instalacaoId: str(raw.instalacaoId ?? raw.instalacao),
    instalacao: str(raw.instalacao ?? raw.instalacaoId),
    versao: str(raw.versao),
    data: nullableStr(raw.data ?? raw.agendado_para),
    hora: str(raw.hora),
    status: (nullableStr(raw.status) as Agendamento['status']) ?? 'Programado',
    origem: str(raw.origem),
    batchId: nullableStr(raw.batchId),
    createdAt: nullableStr(raw.createdAt),
  };
}

export function toEventoHistorico(raw: Raw): EventoHistorico {
  return {
    id: str(raw.id),
    instalacao: str(raw.instalacao),
    versao: str(raw.versao),
    data: nullableStr(raw.data),
    status: (nullableStr(raw.status) as EventoHistorico['status']) ?? 'Concluído',
    origem: str(raw.origem),
    batchId: nullableStr(raw.batchId),
  };
}

export function toNovidade(raw: Raw): Novidade {
  return {
    id: str(raw.id),
    version: str(raw.version),
    category: str(raw.category),
    module: str(raw.module),
    title: str(raw.title),
    summary: str(raw.summary),
    type: str(raw.type),
    PO: str(raw.PO),
    link: str(raw.link),
    imageUrl: str(raw.imageUrl),
  };
}

export const SEM_ATUALIZACOES_ID = '__SEM_ATUALIZACOES__';

export function semAtualizacoesDisponiveis(): VersaoDisponivel {
  return { id: SEM_ATUALIZACOES_ID, nome: 'Nenhuma atualização disponível', semAtualizacoesDisponiveis: true };
}

const TAGS_VERSAO = new Set<TagVersao>(['recomendada', 'homologada', 'nao_homologada', 'retentativa']);

function toTagVersao(valor: unknown): TagVersao | undefined {
  return typeof valor === 'string' && TAGS_VERSAO.has(valor as TagVersao) ? (valor as TagVersao) : undefined;
}

function toVersoesDaGuia(lista: Raw[]): VersaoDisponivel[] {
  // Lista vazia é resposta legítima: a instalação está na versão mais recente
  // (nessa guia).
  if (lista.length === 0) return [semAtualizacoesDisponiveis()];
  return lista.map((item) => ({
    id: str(item.value ?? item.id),
    nome: str(item.placeholder ?? item.nome ?? item.value),
    semAtualizacoesDisponiveis: false,
    tag: toTagVersao(item.tag),
  }));
}

export function toVersoes(payload: unknown): VersoesInstalacao {
  const record = (payload ?? {}) as Raw;
  const guiasRaw = Array.isArray(record.guias) ? (record.guias as Raw[]) : [];
  const guias: GuiaVersao[] = guiasRaw.map((guia) => ({
    chave: str(guia.chave),
    rotulo: str(guia.rotulo ?? guia.chave),
    ramo: str(guia.ramo),
    versoes: toVersoesDaGuia(Array.isArray(guia.versoes) ? (guia.versoes as Raw[]) : []),
  }));
  return {
    versaoAtualCliente: str(record.versaoAtualCliente),
    estagioAtual: record.estagioAtual == null ? null : str(record.estagioAtual),
    guias,
  };
}

export function rotuloHorario(horario: string, disponivel: number, total: number): string {
  if (disponivel <= 0) return `${horario} · sem vagas`;
  return `${horario} · ${disponivel} de ${total} vagas`;
}

export function toDisponibilidade(payload: unknown, dataSolicitada: string): HorarioDisponivel[] {
  const record = (payload ?? {}) as Raw;
  const dataRef = str(record.data, dataSolicitada);
  const horarios = Array.isArray(record.horarios) ? (record.horarios as Raw[]) : [];
  return horarios.map((item) => {
    const horario = str(item.horario);
    const disponivel = num(item.disponivel);
    const total = num(item.total);
    return {
      id: `${dataRef}_${horario}`,
      horario,
      data: dataRef,
      disponivel,
      total,
      nome: rotuloHorario(horario, disponivel, total),
    };
  });
}
